import React, { useState, useEffect, useCallback } from 'react';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  getDocs, 
  getDoc,
  setDoc,
  updateDoc, 
  deleteDoc,
  doc,
  onSnapshot,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from '../../services/firebase';
import { saveClientWithUniqueCpf } from '../../services/clientService';
import { formatCpf, isValidCpf, normalizeCpf } from '../../utils/cpf';
import { groupClientsByIdentity, normalizeEmail } from '../../utils/clientIdentity';
import { 
  Plus, 
  Search, 
  Filter, 
  Edit2, 
  Trash2, 
  Eye,
  Calendar,
  User,
  DollarSign,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  FileText,
  Download,
  Send,
  Link,
  Copy
} from 'lucide-react';
import './Reservations.css';
import { downloadReceipt, openReceipt } from '../../services/receiptService';
import { generatePaymentLink, calculateSystemFee, formatSplitInfo } from '../../services/mercadoPagoService';
import { getQrLink, CHECK_IN_ACTION, CHECK_OUT_ACTION } from '../../utils/checkInOut';
import { sendCheckQrEmail } from '../../utils/sendCheckQrEmail';
import { buildCustomerAccessLink, buildCustomerAccessMessage } from '../../utils/shareCustomerAccess';
import { buildPublicVoucherData, buildVoucherLink, buildVoucherWhatsAppLink, createVoucherToken } from '../../utils/voucher';
import { QRCodeSVG } from 'qrcode.react';
import LoadingState from '../shared/LoadingState';

const Reservations = () => {
  const { t } = useTranslation();
  const { userData } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Helper para pegar companyId correto (colaborador usa currentCompanyId)
  const getCompanyId = useCallback(() => {
    return userData?.role === 'collaborator'
      ? userData?.currentCompanyId
      : userData?.companyId;
  }, [userData]);
  
  const [reservations, setReservations] = useState([]);
  const [filteredReservations, setFilteredReservations] = useState([]);
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showQuickClientModal, setShowQuickClientModal] = useState(false);
  const [showPaymentLinkModal, setShowPaymentLinkModal] = useState(false);
  const [showQrModal, setShowQrModal] = useState(false);
  const [qrModalData, setQrModalData] = useState(null);
  const [paymentLinkData, setPaymentLinkData] = useState(null);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [currentReservation, setCurrentReservation] = useState(null);
  const [companyData, setCompanyData] = useState(null);
  const [quickClientData, setQuickClientData] = useState({
    name: '',
    email: '',
    phone: '',
    cpf: ''
  });
  const [formData, setFormData] = useState({
    clientId: '',
    serviceId: '',
    roomId: '',
    roomName: '',
    date: '',
    time: '',
    checkInDate: '',
    checkOutDate: '',
    dailyCount: 0,
    participants: 1,
    notes: '',
    status: 'pending',
    totalAmount: 0,
    paidAmount: 0,
    paymentStatus: 'unpaid'
  });
  const [planLimitWarning, setPlanLimitWarning] = useState('');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const companyId = getCompanyId();

      // Carregar dados da empresa
      const companyRef = doc(db, `companies/${companyId}`);
      const companySnap = await getDoc(companyRef);
      if (companySnap.exists()) {
        setCompanyData(companySnap.data());
      }


      // Carregar reservas
      const reservationsRef = collection(db, `companies/${companyId}/reservations`);

      // Se for colaborador, carregar somente reservas criadas por ele (visão limitada)
      let reservationsQuery;
      if (userData?.role === 'collaborator' && userData?.email) {
        reservationsQuery = query(reservationsRef, where('createdByEmail', '==', userData.email), orderBy('date', 'desc'));
      } else {
        reservationsQuery = query(reservationsRef, orderBy('date', 'desc'));
      }

      const reservationsSnapshot = await getDocs(reservationsQuery);
      let reservationsData = reservationsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      if (userData?.role === 'collaborator') {
        reservationsData = reservationsData.filter(reservation => ['pending', 'confirmed'].includes(reservation.status));
      }
      setReservations(reservationsData);

      // Carregar clientes
      const clientsRef = collection(db, `companies/${companyId}/clients`);
      const clientsSnapshot = await getDocs(clientsRef);
      const clientsData = clientsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setClients(groupClientsByIdentity(clientsData));

      // Carregar serviços
      const servicesRef = collection(db, `companies/${companyId}/services`);
      const servicesSnapshot = await getDocs(servicesRef);
      const servicesData = servicesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setServices(servicesData);

    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      alert('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, [getCompanyId, userData]);

  const filterReservations = useCallback(() => {
    let filtered = [...reservations];

    // Filtro de busca
    if (searchTerm) {
      filtered = filtered.filter(res => 
        res.clientName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        res.serviceName?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filtro de status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(res => res.status === statusFilter);
    }

    setFilteredReservations(filtered);
  }, [reservations, searchTerm, statusFilter]);

  useEffect(() => {
    if (getCompanyId()) {
      loadData();
    }
  }, [getCompanyId, loadData]);

  useEffect(() => {
    const companyId = getCompanyId();
    if (!companyId) return undefined;

    const reservationsRef = collection(db, `companies/${companyId}/reservations`);
    const reservationsQuery = userData?.role === 'collaborator' && userData?.email
      ? query(reservationsRef, where('createdByEmail', '==', userData.email), orderBy('date', 'desc'))
      : query(reservationsRef, orderBy('date', 'desc'));

    return onSnapshot(reservationsQuery, (snapshot) => {
      let data = snapshot.docs.map(item => ({ id: item.id, ...item.data() }));
      if (userData?.role === 'collaborator') {
        data = data.filter(reservation => ['pending', 'confirmed'].includes(reservation.status));
      }
      setReservations(data);
    }, (error) => {
      console.error('Erro ao acompanhar reservas em tempo real:', error);
    });
  }, [getCompanyId, userData]);

  useEffect(() => {
    filterReservations();
  }, [filterReservations]);

  useEffect(() => {
    const reservationId = searchParams.get('reservation');
    if (!reservationId || reservations.length === 0) return;

    const reservation = reservations.find(item => item.id === reservationId);
    if (reservation) {
      setCurrentReservation(reservation);
      setShowDetailsModal(true);
      setSearchParams({}, { replace: true });
    }
  }, [reservations, searchParams, setSearchParams]);

  const handleOpenModal = async (reservation = null) => {
    if (reservation) {
      setCurrentReservation(reservation);
      const reservationDate = reservation.date.toDate();
      setFormData({
        clientId: reservation.clientId,
        serviceId: reservation.serviceId,
        roomId: reservation.roomId || '',
        roomName: reservation.roomName || '',
        date: reservationDate.toISOString().split('T')[0],
        time: reservationDate.toTimeString().slice(0, 5),
        checkInDate: reservation.checkInDate ? reservation.checkInDate.toDate().toISOString().split('T')[0] : '',
        checkOutDate: reservation.checkOutDate ? reservation.checkOutDate.toDate().toISOString().split('T')[0] : '',
        dailyCount: reservation.dailyCount || 0,
        participants: reservation.participants || 1,
        notes: reservation.notes || '',
        status: reservation.status,
        totalAmount: reservation.totalAmount || 0,
        paidAmount: reservation.paidAmount || 0,
        paymentStatus: reservation.paymentStatus || 'unpaid'
      });
      setPlanLimitWarning('');
    } else {
      setCurrentReservation(null);
      setFormData({
        clientId: '',
        serviceId: '',
        roomId: '',
        roomName: '',
        date: '',
        time: '',
        checkInDate: '',
        checkOutDate: '',
        dailyCount: 0,
        participants: 1,
        notes: '',
        status: 'pending',
        totalAmount: 0,
        paidAmount: 0,
        paymentStatus: 'unpaid'
      });

      // Verificar se estamos próximos do limite para o plano da empresa
      try {
        const systemRef = doc(db, 'systemConfig', 'mercadoPago');
        const systemSnap = await getDoc(systemRef);
        const planFeatures = systemSnap.exists() ? (systemSnap.data().planFeatures || {}) : {};
        const companyPlan = companyData?.plan || 'basic';
        const maxReservations = planFeatures[companyPlan]?.maxReservationsPerMonth ?? null;

        if (maxReservations && maxReservations > 0) {
          const today = new Date();
          const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
          const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
          const monthQuery = query(
            collection(db, `companies/${getCompanyId()}/reservations`),
            where('date', '>=', firstDay),
            where('date', '<', nextMonth)
          );
          const monthSnap = await getDocs(monthQuery);
          const currentCount = monthSnap.size;
          const threshold = Math.max(1, Math.floor(maxReservations * 0.9));
          if (currentCount >= threshold && currentCount < maxReservations) {
            setPlanLimitWarning(`Atenção: já foram feitas ${currentCount} de ${maxReservations} reservas este mês.`);
          } else if (currentCount >= maxReservations) {
            setPlanLimitWarning(`Limite de ${maxReservations} reservas por mês atingido para o plano ${companyPlan}.`);
          } else {
            setPlanLimitWarning('');
          }
        } else {
          setPlanLimitWarning('');
        }
      } catch (err) {
        console.error('Erro ao verificar limite do plano:', err);
        setPlanLimitWarning('');
      }
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setCurrentReservation(null);
  };

  const handleServiceChange = (serviceId) => {
    const service = services.find(s => s.id === serviceId);
    
    // Limpar seleção de quarto e datas ao mudar de serviço
    setFormData(prev => ({ 
      ...prev, 
      serviceId,
      roomId: '',
      roomName: '',
      checkInDate: '',
      checkOutDate: '',
      dailyCount: 0,
      totalAmount: service && !service.isPousada ? (service.price || 0) * (formData.participants || 1) : 0
    }));
  };

  const handleRoomChange = (roomIndex) => {
    const service = services.find(s => s.id === formData.serviceId);
    if (service && service.isPousada && service.rooms && service.rooms[roomIndex]) {
      const selectedRoom = service.rooms[roomIndex];
      // Para pousadas, calcular apenas com base nas diárias (preço é por quarto, não por pessoa)
      const dailyCount = formData.checkInDate && formData.checkOutDate ? formData.dailyCount : 1;
      const totalAmount = (selectedRoom.price || 0) * dailyCount;
      setFormData(prev => ({ 
        ...prev, 
        roomId: roomIndex.toString(),
        roomName: selectedRoom.name,
        totalAmount 
      }));
    }
  };

  const handleCheckInOutChange = (checkInDate, checkOutDate) => {
    if (!checkInDate || !checkOutDate) {
      setFormData(prev => ({ ...prev, checkInDate, checkOutDate, dailyCount: 0, totalAmount: 0 }));
      return;
    }

    const checkIn = new Date(checkInDate);
    const checkOut = new Date(checkOutDate);
    const diffTime = checkOut - checkIn;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 1) {
      alert('A data de saída deve ser posterior à data de entrada');
      return;
    }

    const service = services.find(s => s.id === formData.serviceId);
    let totalAmount = 0;

    if (service && service.isPousada && formData.roomId !== '' && service.rooms) {
      const roomIndex = parseInt(formData.roomId);
      if (service.rooms[roomIndex]) {
        const roomPrice = service.rooms[roomIndex].price || 0;
        totalAmount = roomPrice * diffDays;
      }
    }

    setFormData(prev => ({
      ...prev,
      checkInDate,
      checkOutDate,
      dailyCount: diffDays,
      totalAmount
    }));
  };

  const handleParticipantsChange = (participants) => {
    setFormData(prev => ({ ...prev, participants }));
    const service = services.find(s => s.id === formData.serviceId);
    
    if (service) {
      let price = service.price || 0;
      
      // Se for pousada, usar preço do quarto selecionado
      if (service.isPousada && formData.roomId !== '' && service.rooms) {
        const roomIndex = parseInt(formData.roomId);
        if (service.rooms[roomIndex]) {
          price = service.rooms[roomIndex].price || 0;
        }
      }
      
      const totalAmount = price * participants;
      setFormData(prev => ({ ...prev, totalAmount }));
    }
  };

  const checkAvailability = async (service) => {
    if (!service) return false;

    const companyId = getCompanyId();
    
    try {
      // Para pousadas, verificar disponibilidade do quarto no período
      if (service.isPousada) {
        const roomIndex = parseInt(formData.roomId);
        const room = service.rooms[roomIndex];
        
        if (!room) return false;

        const checkIn = new Date(formData.checkInDate);
        const checkOut = new Date(formData.checkOutDate);
        
        // Buscar reservas do mesmo quarto que se sobrepõem ao período
        const reservationsRef = collection(db, `companies/${companyId}/reservations`);
        const q = query(
          reservationsRef,
          where('serviceId', '==', formData.serviceId),
          where('roomId', '==', formData.roomId),
          where('status', 'in', ['pending', 'confirmed'])
        );
        
        const snapshot = await getDocs(q);
        
        for (const doc of snapshot.docs) {
          const reservation = doc.data();
          
          // Pular a própria reserva se estiver editando
          if (currentReservation && doc.id === currentReservation.id) continue;
          
          if (reservation.checkInDate && reservation.checkOutDate) {
            const resCheckIn = reservation.checkInDate.toDate();
            const resCheckOut = reservation.checkOutDate.toDate();
            
            // Verificar sobreposição de datas
            if (checkIn < resCheckOut && checkOut > resCheckIn) {
              alert(`Este quarto já está reservado para o período de ${resCheckIn.toLocaleDateString('pt-BR')} a ${resCheckOut.toLocaleDateString('pt-BR')}`);
              return false;
            }
          }
        }
        
        // Verificar se o número de participantes não excede a capacidade do quarto
        if (formData.participants > room.capacity) {
          alert(`O quarto selecionado tem capacidade para ${room.capacity} pessoa(s). Você selecionou ${formData.participants} participante(s).`);
          return false;
        }
        
      } else {
        // Para serviços normais, verificar capacidade na data/hora específica
        const dateTime = new Date(`${formData.date}T${formData.time}`);
        const dateStart = new Date(dateTime);
        dateStart.setHours(0, 0, 0, 0);
        const dateEnd = new Date(dateTime);
        dateEnd.setHours(23, 59, 59, 999);
        
        const reservationsRef = collection(db, `companies/${companyId}/reservations`);
        const q = query(
          reservationsRef,
          where('serviceId', '==', formData.serviceId),
          where('date', '>=', dateStart),
          where('date', '<=', dateEnd),
          where('status', 'in', ['pending', 'confirmed'])
        );
        
        const snapshot = await getDocs(q);
        
        let totalParticipants = parseInt(formData.participants);
        
        snapshot.forEach(doc => {
          // Pular a própria reserva se estiver editando
          if (currentReservation && doc.id === currentReservation.id) return;
          
          const reservation = doc.data();
          totalParticipants += reservation.participants || 0;
        });
        
        if (totalParticipants > service.capacity) {
          alert(`Capacidade excedida! O serviço tem capacidade para ${service.capacity} pessoa(s). Já existem ${totalParticipants - formData.participants} participante(s) confirmado(s) para esta data.`);
          return false;
        }
      }
      
      return true;
    } catch (error) {
      console.error('Erro ao verificar disponibilidade:', error);
      alert('Erro ao verificar disponibilidade. Tente novamente.');
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const service = services.find(s => s.id === formData.serviceId);
    
    // Validações básicas
    if (!formData.clientId || !formData.serviceId) {
      alert('Preencha todos os campos obrigatórios');
      return;
    }

    // Validações específicas para pousadas
    if (service?.isPousada) {
      if (!formData.roomId) {
        alert('Selecione um quarto para a pousada');
        return;
      }
      if (!formData.checkInDate || !formData.checkOutDate) {
        alert('Informe as datas de check-in e check-out');
        return;
      }
    } else {
      if (!formData.date || !formData.time) {
        alert('Informe a data e hora do serviço');
        return;
      }
    }

    try {
      const client = clients.find(c => c.id === formData.clientId);
      
      // Validar disponibilidade
      const isAvailable = await checkAvailability(service);
      if (!isAvailable) {
        return; // A mensagem de erro já foi mostrada na função checkAvailability
      }
      
      const dateTime = service?.isPousada 
        ? new Date(formData.checkInDate) 
        : new Date(`${formData.date}T${formData.time}`);
      
      const reservationData = {
        clientId: formData.clientId,
        clientName: client?.name || '',
        clientEmail: client?.email || '',
        clientPhone: client?.phone || '',
        clientCpf: client?.cpf || '',
        serviceId: formData.serviceId,
        serviceName: service?.name || '',
        commissionRate: Number(service?.commission) || 0,
        roomId: formData.roomId || '',
        roomName: formData.roomName || '',
        date: dateTime,
        participants: parseInt(formData.participants),
        notes: formData.notes,
        status: formData.status,
        totalAmount: parseFloat(formData.totalAmount),
        paidAmount: parseFloat(formData.paidAmount),
        paymentStatus: formData.paymentStatus,
        voucherToken: currentReservation?.voucherToken || createVoucherToken(),
        checkInStatus: currentReservation?.checkInStatus || 'pending',
        checkOutStatus: currentReservation?.checkOutStatus || 'pending',
        createdBy: currentReservation?.createdBy || userData.name || userData.email || 'Sistema',
        createdByEmail: currentReservation?.createdByEmail || userData.email || '',
        updatedAt: serverTimestamp()
      };

      // Adicionar campos específicos de pousada
      if (service?.isPousada) {
        reservationData.checkInDate = new Date(formData.checkInDate);
        reservationData.checkOutDate = new Date(formData.checkOutDate);
        reservationData.dailyCount = formData.dailyCount;
      }

      if (currentReservation) {
        // Atualizar
        const reservationRef = doc(db, `companies/${getCompanyId()}/reservations/${currentReservation.id}`);
        await updateDoc(reservationRef, reservationData);
        await setDoc(doc(db, 'publicVouchers', reservationData.voucherToken), buildPublicVoucherData({
          token: reservationData.voucherToken,
          companyId: getCompanyId(),
          companyData,
          reservationId: currentReservation.id,
          reservation: { id: currentReservation.id, ...reservationData }
        }), { merge: true });
        alert('Reserva atualizada com sucesso!');
      } else {
        // Criar
        // Verificar limite de reservas por plano (se definido)
        try {
          const systemRef = doc(db, 'systemConfig', 'mercadoPago');
          const systemSnap = await getDoc(systemRef);
          const planFeatures = systemSnap.exists() ? (systemSnap.data().planFeatures || {}) : {};
          const companyPlan = companyData?.plan || 'basic';
          const maxReservations = planFeatures[companyPlan]?.maxReservationsPerMonth ?? null;

          if (maxReservations && maxReservations > 0) {
            // Contar reservas do mês corrente
            const today = new Date();
            const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
            const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
            const monthQuery = query(
              collection(db, `companies/${getCompanyId()}/reservations`),
              where('date', '>=', firstDay),
              where('date', '<', nextMonth)
            );
            const monthSnap = await getDocs(monthQuery);
            const currentCount = monthSnap.size;

            if (currentCount >= maxReservations) {
              alert(`Limite de ${maxReservations} reservas por mês atingido para o plano ${companyPlan}. Atualize o plano para criar mais reservas.`);
              return;
            }
          }
        } catch (err) {
          console.error('Erro verificando limite de plano:', err);
          // permitir prosseguir em caso de erro na verificação
        }

        reservationData.createdAt = serverTimestamp();
        reservationData.createdBy = userData.name || userData.email || 'Sistema';
        reservationData.createdByEmail = userData.email || '';
        const reservationRef = doc(collection(db, `companies/${getCompanyId()}/reservations`));
        await setDoc(reservationRef, reservationData);
        await setDoc(doc(db, 'publicVouchers', reservationData.voucherToken), buildPublicVoucherData({
          token: reservationData.voucherToken,
          companyId: getCompanyId(),
          companyData,
          reservationId: reservationRef.id,
          reservation: { id: reservationRef.id, ...reservationData }
        }));

        const voucherLink = buildVoucherLink({ token: reservationData.voucherToken });
        const whatsappLink = buildVoucherWhatsAppLink({
          phone: reservationData.clientPhone,
          companyName: companyData?.name,
          reservation: { id: reservationRef.id, ...reservationData },
          voucherLink
        });

        alert('Reserva criada com sucesso!');
        if (whatsappLink) {
          window.open(whatsappLink, '_blank', 'noopener,noreferrer');
        }
      }

      handleCloseModal();
      loadData();
    } catch (error) {
      console.error('Erro ao salvar reserva:', error);
      alert('Erro ao salvar reserva');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta reserva?')) {
      return;
    }

    try {
      await deleteDoc(doc(db, `companies/${getCompanyId()}/reservations/${id}`));
      alert('Reserva excluída com sucesso!');
      loadData();
    } catch (error) {
      console.error('Erro ao excluir reserva:', error);
      alert('Erro ao excluir reserva');
    }
  };

  const handleQuickCreateClient = async () => {
    if (!quickClientData.name || !quickClientData.phone || !quickClientData.cpf) {
      alert('Nome, telefone e CPF são obrigatórios');
      return;
    }
    if (!isValidCpf(quickClientData.cpf)) {
      alert('Informe um CPF válido');
      return;
    }
    const existingClient = clients.find(client => (
      normalizeCpf(client.cpfNormalized || client.cpf) === normalizeCpf(quickClientData.cpf) ||
      (normalizeEmail(quickClientData.email) && client.clientEmails?.includes(normalizeEmail(quickClientData.email)))
    ));
    if (existingClient) {
      setFormData({ ...formData, clientId: existingClient.id });
      setQuickClientData({ name: '', email: '', phone: '', cpf: '' });
      setShowQuickClientModal(false);
      alert('Cliente já cadastrado. O cadastro existente foi selecionado.');
      return;
    }

    try {
      const clientData = {
        name: quickClientData.name,
        email: quickClientData.email,
        phone: quickClientData.phone,
        cpf: quickClientData.cpf,
        totalReservations: 0,
        totalSpent: 0,
        createdBy: userData.email,
        createdByEmail: userData.email
      };

      const savedClient = await saveClientWithUniqueCpf({
        companyId: getCompanyId(),
        data: clientData
      });

      if (!savedClient.existing) {
        const newClient = { id: savedClient.id, ...clientData, cpf: savedClient.cpf, cpfNormalized: savedClient.cpfNormalized };
        setClients([...clients, newClient]);
      }
      
      // Selecionar o novo cliente no formulário
      setFormData({ ...formData, clientId: savedClient.id });
      
      // Limpar e fechar modal
      setQuickClientData({ name: '', email: '', phone: '', cpf: '' });
      setShowQuickClientModal(false);
      
      alert(savedClient.existing ? 'Cliente já cadastrado. O cadastro existente foi selecionado.' : 'Cliente criado com sucesso!');
    } catch (error) {
      console.error('Erro ao criar cliente:', error);
      alert(error?.message || 'Erro ao criar cliente');
    }
  };

  const handleViewDetails = (reservation) => {
    setCurrentReservation(reservation);
    setShowDetailsModal(true);
  };

  const handleCancelReservation = async (reservation) => {
    if (!['pending', 'confirmed'].includes(reservation.status)) return;
    if (reservation.checkInStatus === 'checked_in') {
      alert('Não é possível cancelar uma reserva após o check-in.');
      return;
    }
    if (!window.confirm(`Cancelar a reserva de ${reservation.clientName || 'cliente'}?`)) return;

    try {
      const batch = writeBatch(db);
      const updateData = {
        status: 'cancelled',
        cancelledAt: serverTimestamp(),
        cancelledBy: userData?.email || '',
        updatedAt: serverTimestamp()
      };
      batch.update(doc(db, `companies/${getCompanyId()}/reservations`, reservation.id), updateData);
      if (reservation.voucherToken) {
        batch.set(doc(db, 'publicVouchers', reservation.voucherToken), updateData, { merge: true });
      }
      await batch.commit();
      if (currentReservation?.id === reservation.id) {
        setCurrentReservation(current => ({ ...current, status: 'cancelled' }));
      }
      alert('Reserva cancelada com sucesso.');
    } catch (error) {
      console.error('Erro ao cancelar reserva:', error);
      alert('Não foi possível cancelar a reserva.');
    }
  };

  const handleGenerateReceipt = async (reservation) => {
    try {
      // Buscar dados da empresa
      const companyRef = doc(db, `companies/${getCompanyId()}`);
      const companySnap = await getDoc(companyRef);
      
      let companyData = {};
      if (companySnap.exists()) {
        companyData = companySnap.data();
      }

      // Gerar e baixar comprovante (agora é assíncrono)
      await downloadReceipt(reservation, companyData);
    } catch (error) {
      console.error('Erro ao gerar comprovante:', error);
      alert('Erro ao gerar comprovante');
    }
  };

  const handleViewReceipt = async (reservation) => {
    try {
      // Buscar dados da empresa
      const companyRef = doc(db, `companies/${getCompanyId()}`);
      const companySnap = await getDoc(companyRef);
      
      let companyData = {};
      if (companySnap.exists()) {
        companyData = companySnap.data();
      }

      // Abrir comprovante em nova aba (agora é assíncrono)
      await openReceipt(reservation, companyData);
    } catch (error) {
      console.error('Erro ao visualizar comprovante:', error);
      alert('Erro ao visualizar comprovante');
    }
  };

  const handleSendReceiptByEmail = async (reservation) => {
    if (!reservation.clientPhone) {
      alert('Cliente não possui WhatsApp cadastrado');
      return;
    }

    try {
      const companyRef = doc(db, `companies/${getCompanyId()}`);
      const companySnap = await getDoc(companyRef);
      
      let companyData = {};
      if (companySnap.exists()) {
        companyData = companySnap.data();
      }

      const token = reservation.voucherToken || createVoucherToken();
      if (!reservation.voucherToken) {
        await updateDoc(doc(db, `companies/${getCompanyId()}/reservations/${reservation.id}`), {
          voucherToken: token,
          updatedAt: serverTimestamp()
        });
      }

      await setDoc(doc(db, 'publicVouchers', token), buildPublicVoucherData({
        token,
        companyId: getCompanyId(),
        companyData,
        reservationId: reservation.id,
        reservation: { ...reservation, voucherToken: token }
      }), { merge: true });

      const voucherLink = buildVoucherLink({ token });
      const whatsappLink = buildVoucherWhatsAppLink({
        phone: reservation.clientPhone,
        companyName: companyData.name,
        reservation,
        voucherLink
      });
      window.open(whatsappLink, '_blank', 'noopener,noreferrer');
      alert('Voucher enviado pelo WhatsApp!');
    } catch (error) {
      console.error('Erro ao enviar comprovante pelo WhatsApp:', error);
      alert('Erro ao enviar o resumo pelo WhatsApp.');
    }
  };

  const handleGeneratePaymentLink = async (reservation) => {
    if (reservation.mercadoPagoPayment?.paymentLink) {
      // Já existe um link, apenas mostrar
      setPaymentLinkData(reservation.mercadoPagoPayment);
      setShowPaymentLinkModal(true);
      return;
    }

    setGeneratingLink(true);
    try {
      // Buscar dados do serviço para pegar a comissão
      const serviceRef = doc(db, `companies/${getCompanyId()}/services`, reservation.serviceId);
      const serviceSnap = await getDoc(serviceRef);
      const serviceData = serviceSnap.exists() ? serviceSnap.data() : {};
      const commissionRate = serviceData.commission || 0;

      // Calcular valor pendente
      const pendingAmount = (reservation.totalAmount || 0) - (reservation.paidAmount || 0);

      if (pendingAmount <= 0) {
        alert('Esta reserva já está totalmente paga');
        setGeneratingLink(false);
        return;
      }

      // Gerar link de pagamento
      const result = await generatePaymentLink({
        companyId: getCompanyId(),
        reservationId: reservation.id,
        amount: pendingAmount,
        description: `Reserva: ${reservation.serviceName}${reservation.roomName ? ` - ${reservation.roomName}` : ''}`,
        clientEmail: reservation.clientEmail || 'cliente@example.com',
        clientName: reservation.clientName,
        commissionRate: commissionRate,
        collaboratorEmail: reservation.createdByEmail || userData.email,
        collaboratorName: reservation.createdBy || userData.displayName
      });

      if (result.success) {
        setPaymentLinkData({
          paymentLink: result.paymentLink,
          paymentId: result.paymentId,
          qrCode: result.qrCode,
          qrCodeBase64: result.qrCodeBase64,
          split: result.split
        });
        setShowPaymentLinkModal(true);
        
        // Recarregar reserva atualizada
        await loadData();
      } else {
        alert(result.error || 'Erro ao gerar link de pagamento');
      }
    } catch (error) {
      console.error('Erro ao gerar link de pagamento:', error);
      alert('Erro ao gerar link de pagamento: ' + error.message);
    } finally {
      setGeneratingLink(false);
    }
  };

  const handleCopyPaymentLink = () => {
    if (paymentLinkData?.paymentLink) {
      navigator.clipboard.writeText(paymentLinkData.paymentLink);
      alert('Link copiado para a área de transferência!');
    }
  };

  const handleGenerateCheckQr = (reservation, action) => {
    const link = getQrLink({
      companyId: getCompanyId(),
      reservationId: reservation.id,
      action,
      token: reservation.voucherToken,
      origin: window.location.origin
    });

    if (!link) {
      alert('Não foi possível gerar o link de confirmação.');
      return;
    }

    setQrModalData({
      reservation,
      action,
      link,
      label: action === CHECK_OUT_ACTION ? 'check-out' : 'check-in'
    });
    setShowQrModal(true);
  };

  const handleCopyQrLink = async () => {
    if (!qrModalData?.link) return;

    try {
      await navigator.clipboard.writeText(qrModalData.link);
      alert(`Link de ${qrModalData.label} copiado para a área de transferência!`);
    } catch (error) {
      console.error('Erro ao copiar link do QR:', error);
      alert('Não foi possível copiar o link automaticamente.');
    }
  };

  const handleSendQrToClient = async () => {
    if (!qrModalData?.reservation || !qrModalData?.link) return;

    try {
      const result = await sendCheckQrEmail({
        companyData,
        reservation: qrModalData.reservation,
        action: qrModalData.action,
        link: qrModalData.link
      });

      if (result.success) {
        alert('QR enviado pelo WhatsApp para o cliente!');
      } else {
        alert(result.error || 'Erro ao enviar o QR pelo WhatsApp.');
      }
    } catch (error) {
      console.error('Erro ao enviar QR pelo WhatsApp:', error);
      alert('Erro ao enviar o QR pelo WhatsApp.');
    }
  };

  const handleShareCustomerAccess = (reservation) => {
    const accessLink = buildCustomerAccessLink({
      companySlug: companyData?.slug || userData?.companySlug,
      reservationId: reservation.id
    });

    const message = buildCustomerAccessMessage({
      companyName: companyData?.name || 'nossa empresa',
      reservationId: reservation.id,
      accessLink
    });

    const phone = reservation.clientPhone?.replace(/\D/g, '');
    if (!phone) {
      alert('Cadastre o WhatsApp do cliente para compartilhar o acesso.');
      return;
    }

    const whatsappLink = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
    window.open(whatsappLink, '_blank', 'noopener,noreferrer');
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'Pendente', color: '#ff9800', icon: Clock },
      confirmed: { label: 'Confirmada', color: '#4caf50', icon: CheckCircle },
      cancelled: { label: 'Cancelada', color: '#f44336', icon: XCircle },
      no_show: { label: 'Não compareceu', color: '#64748b', icon: AlertCircle },
      completed: { label: 'Concluída', color: '#2196f3', icon: CheckCircle }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <span className="status-badge" style={{ backgroundColor: config.color }}>
        <Icon size={14} />
        {config.label}
      </span>
    );
  };

  const getPaymentStatusBadge = (paymentStatus) => {
    const statusConfig = {
      unpaid: { label: 'Não Pago', color: '#f44336' },
      partial: { label: 'Parcial', color: '#ff9800' },
      paid: { label: 'Pago', color: '#4caf50' }
    };
    
    const config = statusConfig[paymentStatus] || statusConfig.unpaid;
    
    return (
      <span className="payment-badge" style={{ backgroundColor: config.color }}>
        {config.label}
      </span>
    );
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatDate = (date) => {
    if (!date) return '';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="reservations-container">
        <LoadingState title="Carregando reservas" subtitle="Atualizando agenda, clientes e servicos" />
      </div>
    );
  }

  return (
    <div className="reservations-container">
      {/* Header */}
      <div className="reservations-header">
        <div>
          <h1>Reservas</h1>
          <p>{filteredReservations.length} reserva(s) encontrada(s)</p>
        </div>
        <button className="btn-primary" onClick={() => handleOpenModal()}>
          <Plus size={20} />
          Nova Reserva
        </button>
      </div>

      {/* Filtros */}
      <div className="reservations-filters">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Buscar por cliente ou serviço..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="filter-group">
          <Filter size={18} />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">Todos os Status</option>
            <option value="pending">Pendente</option>
            <option value="confirmed">Confirmada</option>
            <option value="completed">Concluída</option>
            <option value="cancelled">Cancelada</option>
            <option value="no_show">Não compareceu</option>
          </select>
        </div>
      </div>

      {/* Tabela de Reservas */}
      <div className="reservations-table-container">
        {filteredReservations.length === 0 ? (
          <div className="empty-state">
            <Calendar size={48} color="#ccc" />
            <p>Nenhuma reserva encontrada</p>
            <button className="btn-secondary" onClick={() => handleOpenModal()}>
              Criar primeira reserva
            </button>
          </div>
        ) : (
          <table className="reservations-table">
            <thead>
              <tr>
                <th>Cliente</th>
                <th>Serviço</th>
                <th>Quarto</th>
                <th>Data/Hora</th>
                <th>Participantes</th>
                <th>Valor</th>
                <th>Status</th>
                <th>Pagamento</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {filteredReservations.map((reservation) => (
                <tr key={reservation.id}>
                  <td data-label="Cliente">
                    <div className="client-cell">
                      <User size={16} />
                      <span>{reservation.clientName}</span>
                    </div>
                  </td>
                  <td data-label="Serviço">{reservation.serviceName}</td>
                  <td data-label="Quarto">
                    {reservation.roomName ? (
                      <span className="room-badge">{reservation.roomName}</span>
                    ) : (
                      <span className="room-empty">-</span>
                    )}
                  </td>
                  <td data-label="Data/Hora">
                    <div className="date-cell">
                      <Calendar size={14} />
                      {reservation.checkInDate && reservation.checkOutDate ? (
                        <span>
                          {reservation.checkInDate.toDate().toLocaleDateString('pt-BR')} - {reservation.checkOutDate.toDate().toLocaleDateString('pt-BR')}
                          <br />
                          <small style={{ color: '#666' }}>({reservation.dailyCount} diária{reservation.dailyCount > 1 ? 's' : ''})</small>
                        </span>
                      ) : (
                        formatDate(reservation.date)
                      )}
                    </div>
                  </td>
                  <td data-label="Participantes" className="text-center">{reservation.participants}</td>
                  <td data-label="Valor" className="text-right">{formatCurrency(reservation.totalAmount)}</td>
                  <td data-label="Status">{getStatusBadge(reservation.status)}</td>
                  <td data-label="Pagamento">{getPaymentStatusBadge(reservation.paymentStatus)}</td>
                  <td data-label="Ações">
                    <div className="action-buttons">
                      {(reservation.status === 'confirmed' || reservation.status === 'completed') && (
                        <>
                          <button 
                            className="btn-icon btn-success" 
                            onClick={() => handleGenerateReceipt(reservation)}
                            title="Baixar Comprovante"
                          >
                            <Download size={18} />
                          </button>
                          {reservation.clientEmail && (
                            <button 
                              className="btn-icon btn-primary" 
                              onClick={() => handleSendReceiptByEmail(reservation)}
                              title="Enviar pelo WhatsApp"
                            >
                              <Send size={18} />
                            </button>
                          )}
                        </>
                      )}
                      {((reservation.totalAmount || 0) - (reservation.paidAmount || 0) > 0) && (
                        <button 
                          className="btn-icon btn-warning" 
                          onClick={() => handleGeneratePaymentLink(reservation)}
                          title={
                            generatingLink 
                              ? "Gerando link..." 
                              : !companyData?.mercadoPagoAccessToken 
                              ? "Mercado Pago não configurado" 
                              : !reservation.createdByEmail
                              ? "Colaborador sem email para comissão"
                              : reservation.mercadoPagoPayment?.paymentLink 
                              ? "Ver Link de Pagamento" 
                              : "Gerar Link de Pagamento"
                          }
                          disabled={generatingLink || !companyData?.mercadoPagoAccessToken || !reservation.createdByEmail}
                        >
                          <Link size={18} />
                        </button>
                      )}
                      <button 
                        className="btn-icon" 
                        onClick={() => handleViewDetails(reservation)}
                        title="Ver detalhes"
                      >
                        <Eye size={18} />
                      </button>
                      <button 
                        className="btn-icon" 
                        onClick={() => handleOpenModal(reservation)}
                        title="Editar"
                      >
                        <Edit2 size={18} />
                      </button>
                      {['pending', 'confirmed'].includes(reservation.status) && reservation.checkInStatus !== 'checked_in' && (
                        <button
                          className="btn-icon btn-danger"
                          onClick={() => handleCancelReservation(reservation)}
                          title="Cancelar reserva"
                        >
                          <XCircle size={18} />
                        </button>
                      )}
                      <button 
                        className="btn-icon btn-danger" 
                        onClick={() => handleDelete(reservation.id)}
                        title="Excluir"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal de Criação/Edição */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{currentReservation ? 'Editar Reserva' : 'Nova Reserva'}</h2>
              <button className="btn-close" onClick={handleCloseModal}>×</button>
            </div>

            <form onSubmit={handleSubmit} className="reservation-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Cliente *</label>
                  <div className="client-select-wrapper">
                    <Select
                      classNamePrefix="react-select"
                      value={clients.find(c => c.id === formData.clientId) ? { value: formData.clientId, label: `${clients.find(c => c.id === formData.clientId)?.name} · ${formatCpf(clients.find(c => c.id === formData.clientId)?.cpf) || 'Sem CPF'}` } : null}
                      onChange={option => setFormData({ ...formData, clientId: option ? option.value : '' })}
                      options={clients.map(client => ({ value: client.id, label: `${client.name} · ${formatCpf(client.cpf) || 'Sem CPF'}` }))}
                      placeholder="Busque por nome ou CPF"
                      isClearable
                      isSearchable
                      required
                    />
                    <button
                      type="button"
                      className="btn-quick-add"
                      onClick={() => setShowQuickClientModal(true)}
                      title="Cadastrar novo cliente"
                    >
                      <Plus size={18} />
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label>Serviço *</label>
                  <Select
                    classNamePrefix="react-select"
                    value={services.find(s => s.id === formData.serviceId) ? { value: formData.serviceId, label: services.find(s => s.id === formData.serviceId)?.name + ' - ' + (services.find(s => s.id === formData.serviceId)?.isPousada ? 'Pousada' : formatCurrency(services.find(s => s.id === formData.serviceId)?.price)) } : null}
                    onChange={option => handleServiceChange(option ? option.value : '')}
                    options={services.map(service => ({ value: service.id, label: service.name + ' - ' + (service.isPousada ? 'Pousada' : formatCurrency(service.price)) }))}
                    placeholder="Selecione um serviço"
                    isClearable
                    isSearchable
                    required
                  />
                </div>
              </div>

              {planLimitWarning && (
                <div className="alert alert-warning">
                  {planLimitWarning}
                </div>
              )}

              {/* Seleção de Quarto para Pousadas */}
              {formData.serviceId && services.find(s => s.id === formData.serviceId)?.isPousada && (
                <div className="form-row">
                  <div className="form-group">
                    <label>Quarto *</label>
                    <Select
                      classNamePrefix="react-select"
                      value={formData.roomId !== '' ? {
                        value: formData.roomId,
                        label: (() => {
                          const service = services.find(s => s.id === formData.serviceId);
                          if (service && service.rooms) {
                            const room = service.rooms[parseInt(formData.roomId)];
                            if (room) {
                              return `${room.name} - ${formatCurrency(room.price)} - Capacidade: ${room.capacity}`;
                            }
                          }
                          return '';
                        })()
                      } : null}
                      onChange={option => handleRoomChange(option ? parseInt(option.value) : '')}
                      options={(() => {
                        const service = services.find(s => s.id === formData.serviceId);
                        if (service && service.rooms) {
                          return service.rooms.map((room, index) => ({
                            value: index.toString(),
                            label: `${room.name} - ${formatCurrency(room.price)} - Capacidade: ${room.capacity}`
                          }));
                        }
                        return [];
                      })()}
                      placeholder="Selecione um quarto"
                      isClearable
                      isSearchable
                      required
                    />
                  </div>
                </div>
              )}

              {/* Campos de Data/Hora - Diferentes para Pousadas e Serviços Normais */}
              {formData.serviceId && services.find(s => s.id === formData.serviceId)?.isPousada ? (
                // Para Pousadas: Check-in e Check-out
                <div className="form-row">
                  <div className="form-group">
                    <label>Check-in *</label>
                    <input
                      type="date"
                      value={formData.checkInDate}
                      onChange={(e) => handleCheckInOutChange(e.target.value, formData.checkOutDate)}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Check-out *</label>
                    <input
                      type="date"
                      value={formData.checkOutDate}
                      onChange={(e) => handleCheckInOutChange(formData.checkInDate, e.target.value)}
                      min={formData.checkInDate}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Diárias</label>
                    <input
                      type="number"
                      value={formData.dailyCount}
                      disabled
                      style={{ background: '#f5f5f5' }}
                    />
                  </div>
                </div>
              ) : (
                // Para Serviços Normais: Data e Hora
                <div className="form-row">
                  <div className="form-group">
                    <label>Data *</label>
                    <input
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      required={formData.serviceId && !services.find(s => s.id === formData.serviceId)?.isPousada}
                    />
                  </div>

                  <div className="form-group">
                    <label>Hora *</label>
                    <input
                      type="time"
                      value={formData.time}
                      onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                      required={formData.serviceId && !services.find(s => s.id === formData.serviceId)?.isPousada}
                    />
                  </div>

                  <div className="form-group">
                    <label>Participantes *</label>
                    <input
                      type="number"
                      min="1"
                      value={formData.participants}
                      onChange={(e) => handleParticipantsChange(parseInt(e.target.value))}
                      required
                    />
                  </div>
                </div>
              )}

              {/* Participantes para Pousadas */}
              {formData.serviceId && services.find(s => s.id === formData.serviceId)?.isPousada && (
                <div className="form-row">
                  <div className="form-group">
                    <label>Participantes *</label>
                    <input
                      type="number"
                      min="1"
                      value={formData.participants}
                      onChange={(e) => setFormData({ ...formData, participants: parseInt(e.target.value) })}
                      required
                    />
                  </div>
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label>Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  >
                    <option value="pending">Pendente</option>
                    <option value="confirmed">Confirmada</option>
                    <option value="completed">Concluída</option>
                    <option value="cancelled">Cancelada</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Status Pagamento</label>
                  <select
                    value={formData.paymentStatus}
                    onChange={(e) => setFormData({ ...formData, paymentStatus: e.target.value })}
                  >
                    <option value="unpaid">Não Pago</option>
                    <option value="partial">Parcial</option>
                    <option value="paid">Pago</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Valor Total</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.totalAmount}
                    onChange={(e) => setFormData({ ...formData, totalAmount: e.target.value })}
                    readOnly
                  />
                </div>

                <div className="form-group">
                  <label>Valor Pago</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.paidAmount}
                    onChange={(e) => setFormData({ ...formData, paidAmount: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Observações</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows="3"
                  placeholder="Observações adicionais..."
                />
              </div>

              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={handleCloseModal}>
                  Cancelar
                </button>
                <button type="submit" className="btn-primary">
                  {currentReservation ? 'Atualizar' : 'Criar'} Reserva
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Detalhes */}
      {showDetailsModal && currentReservation && (
        <div className="modal-overlay" onClick={() => setShowDetailsModal(false)}>
          <div className="modal-content details-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Detalhes da Reserva</h2>
              <button className="btn-close" onClick={() => setShowDetailsModal(false)}>×</button>
            </div>

            <div className="details-content">
              <div className="details-section">
                <h3>Cliente</h3>
                <div className="detail-item">
                  <User size={18} />
                  <div>
                    <p className="detail-label">Nome</p>
                    <p className="detail-value">{currentReservation.clientName}</p>
                  </div>
                </div>
                {currentReservation.clientEmail && (
                  <div className="detail-item">
                    <span>📧</span>
                    <div>
                      <p className="detail-label">Email</p>
                      <p className="detail-value">{currentReservation.clientEmail}</p>
                    </div>
                  </div>
                )}
                {currentReservation.clientPhone && (
                  <div className="detail-item">
                    <span>📱</span>
                    <div>
                      <p className="detail-label">Telefone</p>
                      <p className="detail-value">{currentReservation.clientPhone}</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="details-section">
                <h3>Serviço</h3>
                <div className="detail-item">
                  <Calendar size={18} />
                  <div>
                    <p className="detail-label">Serviço</p>
                    <p className="detail-value">{currentReservation.serviceName}</p>
                  </div>
                </div>
                {currentReservation.roomName && (
                  <div className="detail-item">
                    <span>🚪</span>
                    <div>
                      <p className="detail-label">Quarto</p>
                      <p className="detail-value">{currentReservation.roomName}</p>
                    </div>
                  </div>
                )}
                {currentReservation.checkInDate && currentReservation.checkOutDate ? (
                  <>
                    <div className="detail-item">
                      <Calendar size={18} />
                      <div>
                        <p className="detail-label">Check-in</p>
                        <p className="detail-value">{currentReservation.checkInDate.toDate().toLocaleDateString('pt-BR')}</p>
                      </div>
                    </div>
                    <div className="detail-item">
                      <Calendar size={18} />
                      <div>
                        <p className="detail-label">Check-out</p>
                        <p className="detail-value">{currentReservation.checkOutDate.toDate().toLocaleDateString('pt-BR')}</p>
                      </div>
                    </div>
                    <div className="detail-item">
                      <Clock size={18} />
                      <div>
                        <p className="detail-label">Diárias</p>
                        <p className="detail-value">{currentReservation.dailyCount} diária{currentReservation.dailyCount > 1 ? 's' : ''}</p>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="detail-item">
                    <Clock size={18} />
                    <div>
                      <p className="detail-label">Data e Hora</p>
                      <p className="detail-value">{formatDate(currentReservation.date)}</p>
                    </div>
                  </div>
                )}
                <div className="detail-item">
                  <User size={18} />
                  <div>
                    <p className="detail-label">Participantes</p>
                    <p className="detail-value">{currentReservation.participants}</p>
                  </div>
                </div>
              </div>

              <div className="details-section">
                <h3>Pagamento</h3>
                <div className="detail-item">
                  <DollarSign size={18} />
                  <div>
                    <p className="detail-label">Valor Total</p>
                    <p className="detail-value">{formatCurrency(currentReservation.totalAmount)}</p>
                  </div>
                </div>
                <div className="detail-item">
                  <DollarSign size={18} />
                  <div>
                    <p className="detail-label">Valor Pago</p>
                    <p className="detail-value">{formatCurrency(currentReservation.paidAmount)}</p>
                  </div>
                </div>
                <div className="detail-item">
                  <DollarSign size={18} />
                  <div>
                    <p className="detail-label">Saldo Devedor</p>
                    <p className="detail-value">
                      {formatCurrency(currentReservation.totalAmount - currentReservation.paidAmount)}
                    </p>
                  </div>
                </div>
                <div className="detail-item">
                  <AlertCircle size={18} />
                  <div>
                    <p className="detail-label">Status Pagamento</p>
                    <p className="detail-value">{getPaymentStatusBadge(currentReservation.paymentStatus)}</p>
                  </div>
                </div>
              </div>

              <div className="details-section">
                <h3>Status e Observações</h3>
                <div className="detail-item">
                  <CheckCircle size={18} />
                  <div>
                    <p className="detail-label">Status</p>
                    <p className="detail-value">{getStatusBadge(currentReservation.status)}</p>
                  </div>
                </div>
                {currentReservation.notes && (
                  <div className="detail-item">
                    <span>📝</span>
                    <div>
                      <p className="detail-label">Observações</p>
                      <p className="detail-value">{currentReservation.notes}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="details-actions">
              <button className="btn-secondary" onClick={() => setShowDetailsModal(false)}>
                Fechar
              </button>
              {['pending', 'confirmed'].includes(currentReservation.status) && currentReservation.checkInStatus !== 'checked_in' && (
                <button className="btn-danger" onClick={() => handleCancelReservation(currentReservation)}>
                  <XCircle size={18} />
                  Cancelar reserva
                </button>
              )}
              
              {(currentReservation.status === 'confirmed' || currentReservation.status === 'completed') && (
                <>
                  <button className="btn-success" onClick={() => {
                    handleViewReceipt(currentReservation);
                  }}>
                    <FileText size={18} />
                    Ver Comprovante
                  </button>
                  <button className="btn-success" onClick={() => {
                    handleGenerateReceipt(currentReservation);
                  }}>
                    <Download size={18} />
                    Baixar PDF
                  </button>
                  {currentReservation.clientEmail && (
                    <button className="btn-primary" onClick={() => {
                      handleSendReceiptByEmail(currentReservation);
                    }}>
                      <Send size={18} />
                      Enviar pelo WhatsApp
                    </button>
                  )}
                </>
              )}
              
              {((currentReservation.totalAmount || 0) - (currentReservation.paidAmount || 0) > 0) && (
                <button 
                  className="btn-warning" 
                  onClick={() => {
                    setShowDetailsModal(false);
                    handleGeneratePaymentLink(currentReservation);
                  }}
                  disabled={generatingLink || !companyData?.mercadoPagoAccessToken || !currentReservation.createdByEmail}
                  title={
                    generatingLink 
                      ? "Gerando link..." 
                      : !companyData?.mercadoPagoAccessToken 
                      ? "Mercado Pago não configurado" 
                      : !currentReservation.createdByEmail
                      ? "Colaborador sem email para comissão"
                      : currentReservation.mercadoPagoPayment?.paymentLink 
                      ? "Ver Link de Pagamento" 
                      : "Gerar Link de Pagamento"
                  }
                >
                  <Link size={18} />
                  {currentReservation.mercadoPagoPayment?.paymentLink ? 'Ver Link de Pagamento' : 'Gerar Link de Pagamento'}
                </button>
              )}
              
              <button className="btn-primary" onClick={() => {
                setShowDetailsModal(false);
                handleOpenModal(currentReservation);
              }}>
                <Edit2 size={18} />
                Editar Reserva
              </button>

              <button className="btn-success" onClick={() => handleShareCustomerAccess(currentReservation)}>
                <Send size={18} />
                Compartilhar acesso ao cliente
              </button>

              <button className="btn-success" onClick={() => handleGenerateCheckQr(currentReservation, CHECK_IN_ACTION)}>
                <CheckCircle size={18} />
                Abrir QR Check-in
              </button>

              <button className="btn-warning" onClick={() => handleGenerateCheckQr(currentReservation, CHECK_OUT_ACTION)}>
                <Clock size={18} />
                Abrir QR Check-out
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de QR para check-in/check-out */}
      {showQrModal && qrModalData && (
        <div className="modal-overlay" onClick={() => setShowQrModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 420, textAlign: 'center' }}>
            <div className="modal-header">
              <h2>QR Code para {qrModalData.label === 'check-out' ? 'Check-out' : 'Check-in'}</h2>
              <button className="btn-close" onClick={() => setShowQrModal(false)}>×</button>
            </div>

            <div style={{ padding: '16px 8px 8px' }}>
              <p style={{ marginBottom: 16, color: '#4b5563' }}>
                Mostre este QR para o cliente escanear e confirmar {qrModalData.label === 'check-out' ? 'o check-out' : 'o check-in'}.
              </p>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
                <QRCodeSVG value={qrModalData.link} size={220} includeMargin />
              </div>
              <p style={{ fontSize: 12, color: '#6b7280', wordBreak: 'break-all', marginBottom: 16 }}>{qrModalData.link}</p>
              <div className="details-actions" style={{ justifyContent: 'center' }}>
                <button className="btn-primary" onClick={handleCopyQrLink}>Copiar link</button>
                <button className="btn-success" onClick={handleSendQrToClient}>Enviar pelo WhatsApp</button>
                <button className="btn-secondary" onClick={() => setShowQrModal(false)}>Fechar</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Rápido de Cadastro de Cliente */}
      {showQuickClientModal && (
        <div className="modal-overlay" onClick={() => setShowQuickClientModal(false)}>
          <div className="modal-content quick-client-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Cadastro Rápido de Cliente</h2>
              <button className="btn-close" onClick={() => setShowQuickClientModal(false)}>×</button>
            </div>

            <div className="quick-client-form">
              <div className="form-group">
                <label>Nome Completo *</label>
                <input
                  type="text"
                  value={quickClientData.name}
                  onChange={(e) => setQuickClientData({ ...quickClientData, name: e.target.value })}
                  placeholder="Nome do cliente"
                  autoFocus
                />
              </div>

              <div className="form-group">
                <label>Telefone *</label>
                <input
                  type="tel"
                  value={quickClientData.phone}
                  onChange={(e) => setQuickClientData({ ...quickClientData, phone: e.target.value })}
                  placeholder="(11) 98765-4321"
                />
              </div>

              <div className="form-group">
                <label>CPF *</label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength="14"
                  value={quickClientData.cpf}
                  onChange={(e) => setQuickClientData({ ...quickClientData, cpf: formatCpf(e.target.value) })}
                  placeholder="000.000.000-00"
                />
              </div>

              <div className="form-group">
                <label>Email (opcional)</label>
                <input
                  type="email"
                  value={quickClientData.email}
                  onChange={(e) => setQuickClientData({ ...quickClientData, email: e.target.value })}
                  placeholder="cliente@email.com"
                />
              </div>

              <div className="form-actions">
                <button 
                  type="button" 
                  className="btn-secondary" 
                  onClick={() => setShowQuickClientModal(false)}
                >
                  Cancelar
                </button>
                <button 
                  type="button" 
                  className="btn-primary" 
                  onClick={handleQuickCreateClient}
                >
                  <Plus size={18} />
                  Criar e Selecionar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Link de Pagamento Mercado Pago */}
      {showPaymentLinkModal && paymentLinkData && (
        <div className="modal-overlay" onClick={() => setShowPaymentLinkModal(false)}>
          <div className="modal-content payment-link-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Link de Pagamento - Mercado Pago</h2>
              <button className="btn-close" onClick={() => setShowPaymentLinkModal(false)}>×</button>
            </div>

            <div className="payment-link-content">
              {/* Informações da Reserva */}
              <div className="payment-link-section">
                <h3>Informações da Reserva</h3>
                <div className="payment-info-grid">
                  <div className="payment-info-item">
                    <span className="label">Cliente:</span>
                    <span className="value">{paymentLinkData.clientName}</span>
                  </div>
                  <div className="payment-info-item">
                    <span className="label">Serviço:</span>
                    <span className="value">{paymentLinkData.serviceName}</span>
                  </div>
                  <div className="payment-info-item">
                    <span className="label">Valor Total:</span>
                    <span className="value">{formatCurrency(paymentLinkData.totalAmount)}</span>
                  </div>
                  <div className="payment-info-item">
                    <span className="label">Valor Pendente:</span>
                    <span className="value strong">{formatCurrency(paymentLinkData.pendingAmount)}</span>
                  </div>
                </div>
              </div>

              {/* Split de Pagamento */}
              {paymentLinkData.split && (
                <div className="payment-link-section">
                  <h3>Divisão do Pagamento</h3>
                  <div className="split-info-grid">
                    <div className="split-info-item">
                      <div className="split-label">
                        <span className="icon">💰</span>
                        <span>Sistema (Taxa {paymentLinkData.split.systemFeeRate}%)</span>
                      </div>
                      <span className="split-value">{formatCurrency(paymentLinkData.split.systemAmount)}</span>
                    </div>
                    <div className="split-info-item">
                      <div className="split-label">
                        <span className="icon">👤</span>
                        <span>Colaborador (Comissão {paymentLinkData.split.commissionRate}%)</span>
                      </div>
                      <span className="split-value">{formatCurrency(paymentLinkData.split.collaboratorAmount)}</span>
                    </div>
                    <div className="split-info-item highlight">
                      <div className="split-label">
                        <span className="icon">🏢</span>
                        <span>Empresa ({paymentLinkData.split.companyPercentage}%)</span>
                      </div>
                      <span className="split-value">{formatCurrency(paymentLinkData.split.companyAmount)}</span>
                    </div>
                  </div>
                  <div className="split-note">
                    <AlertCircle size={16} />
                    <span>O pagamento será dividido automaticamente após a confirmação.</span>
                  </div>
                </div>
              )}

              {/* Link de Pagamento */}
              <div className="payment-link-section">
                <h3>Link de Pagamento</h3>
                <div className="payment-link-box">
                  <input
                    type="text"
                    value={paymentLinkData.paymentLink}
                    readOnly
                    className="payment-link-input"
                  />
                  <button
                    className="btn-copy"
                    onClick={() => handleCopyPaymentLink(paymentLinkData.paymentLink)}
                    title="Copiar link"
                  >
                    <Copy size={18} />
                  </button>
                </div>
                <p className="payment-link-help">
                  Envie este link para o cliente fazer o pagamento de forma segura via Mercado Pago.
                </p>
              </div>

              {/* QR Code (se disponível) */}
              {paymentLinkData.qrCode && (
                <div className="payment-link-section qr-code-section">
                  <h3>QR Code</h3>
                  <div className="qr-code-container">
                    <img src={paymentLinkData.qrCode} alt="QR Code Mercado Pago" />
                  </div>
                  <p className="qr-code-help">
                    O cliente pode escanear este QR Code com o app do Mercado Pago.
                  </p>
                </div>
              )}

              {/* Status do Pagamento */}
              {paymentLinkData.paymentStatus && (
                <div className="payment-link-section">
                  <h3>Status do Pagamento</h3>
                  <div className="payment-status-box">
                    <span className={`payment-status-badge status-${paymentLinkData.paymentStatus}`}>
                      {paymentLinkData.paymentStatus === 'pending' && '⏳ Aguardando Pagamento'}
                      {paymentLinkData.paymentStatus === 'approved' && '✅ Pagamento Aprovado'}
                      {paymentLinkData.paymentStatus === 'rejected' && '❌ Pagamento Rejeitado'}
                      {paymentLinkData.paymentStatus === 'cancelled' && '🚫 Pagamento Cancelado'}
                    </span>
                    {paymentLinkData.paymentDate && (
                      <span className="payment-date">
                        Pago em: {format(new Date(paymentLinkData.paymentDate), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowPaymentLinkModal(false)}>
                Fechar
              </button>
              <button 
                className="btn-primary" 
                onClick={() => handleCopyPaymentLink(paymentLinkData.paymentLink)}
              >
                <Copy size={18} />
                Copiar Link
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Reservations;
